/**
 * Supplier detail — what and how much we purchased from one supplier.
 * Aggregates approved e-invoice lines by ingredient: quantity, spend,
 * last/avg price with trend sparkline.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabase.js';
import { fetchPaged } from '../fetchPaged.js';
import {
  Button,
  Callout,
  DateRange,
  PageHeader,
  resolveDateRange,
  type DateRangeValue,
} from '../ui/index.js';
import { formatAmd, formatNumber } from '../locale.js';

const CHUNK = 200;

export interface SupplierSummaryTarget {
  id: string;
  tin: string;
  display_name: string;
  name: string;
  category: string;
}

interface PricePoint {
  date: string; // invoice issued_at
  price: number;
}

interface ItemAgg {
  key: string;
  label: string;
  tracked: boolean;
  unit: string;
  qty: number;
  spend: number;
  invoiceIds: Set<string>;
  points: PricePoint[];
}

const fmtAMD = (n: number, lang?: string | null) => formatAmd(n, lang);
const fmtPrice = (n: number, lang?: string | null) =>
  `${formatNumber(n, lang, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ֏`;
const fmtQty = (n: number, lang?: string | null) =>
  formatNumber(n, lang, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Tiny inline price-trend sparkline. */
function Sparkline({ points, empty = '—' }: { points: PricePoint[]; empty?: string }) {
  if (points.length < 2) {
    return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{empty}</span>;
  }
  const w = 90;
  const h = 24;
  const pad = 2;
  const prices = points.map((p) => p.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min || 1;
  const step = (w - pad * 2) / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - ((p.price - min) / span) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const rising = prices[prices.length - 1]! > prices[0]!;
  return (
    <svg width={w} height={h} style={{ display: 'block' }} aria-hidden>
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke={rising ? 'var(--danger)' : 'var(--success, #10b981)'}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SupplierDetail({
  clientId,
  supplier,
  onBack,
}: {
  clientId: string | null;
  supplier: SupplierSummaryTarget;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const empty = t('common.emptyDash');
  const [range, setRange] = useState<DateRangeValue>({ preset: '90d' });
  const { from: fromDate = '', to: toDate = '' } = resolveDateRange(range);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ItemAgg[]>([]);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [totalSpend, setTotalSpend] = useState(0);

  const load = useCallback(async () => {
    if (!supabase || !clientId) return;
    const db = supabase;
    setLoading(true);
    setError(null);
    try {
      type InvRow = { id: string; issued_at: string | null; total: number };
      const invoices = (await fetchPaged((offset, limit) =>
        db
          .from('invoices')
          .select('id, issued_at, total')
          .eq('client_id', clientId)
          .eq('supplier_tin', supplier.tin)
          .eq('internal_status', 'approved')
          .gte('issued_at', fromDate)
          .lte('issued_at', `${toDate}T23:59:59`)
          .order('issued_at', { ascending: true })
          .range(offset, limit),
      )) as unknown as InvRow[];

      const issuedById = new Map(invoices.map((i) => [i.id, i.issued_at ?? '']));

      type GoodsRow = {
        invoice_id: string;
        ingredient_id: string | null;
        goods_name: string;
        quantity: number;
        unit: string;
        unit_price: number;
        total: number;
      };
      const goods: GoodsRow[] = [];
      const ids = invoices.map((i) => i.id);
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const batch = (await fetchPaged((offset, limit) =>
          db
            .from('invoice_goods')
            .select('invoice_id, ingredient_id, goods_name, quantity, unit, unit_price, total')
            .in('invoice_id', chunk)
            .range(offset, limit),
        )) as unknown as GoodsRow[];
        goods.push(...batch);
      }

      const { data: ingRows } = await db.from('ingredients').select('id, name, unit');
      const ingById = new Map((ingRows ?? []).map((r) => [r.id, r as { id: string; name: string; unit: string }]));

      const byKey = new Map<string, ItemAgg>();
      for (const g of goods) {
        const ing = g.ingredient_id ? ingById.get(g.ingredient_id) : undefined;
        const key = g.ingredient_id ?? `untracked:${g.goods_name}`;
        let item = byKey.get(key);
        if (!item) {
          item = {
            key,
            label: ing?.name ?? g.goods_name,
            tracked: !!g.ingredient_id,
            unit: ing?.unit ?? g.unit,
            qty: 0,
            spend: 0,
            invoiceIds: new Set(),
            points: [],
          };
          byKey.set(key, item);
        }
        item.qty += Number(g.quantity);
        item.spend += Number(g.total);
        item.invoiceIds.add(g.invoice_id);
        if (Number(g.unit_price) > 0) {
          item.points.push({ date: issuedById.get(g.invoice_id) ?? '', price: Number(g.unit_price) });
        }
      }
      for (const item of byKey.values()) {
        item.points.sort((a, b) => a.date.localeCompare(b.date));
      }

      setItems([...byKey.values()].sort((a, b) => b.spend - a.spend));
      setInvoiceCount(invoices.length);
      setTotalSpend(invoices.reduce((s, i) => s + Number(i.total), 0));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('supplierDetail.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [clientId, supplier.tin, range, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const itemsSpend = useMemo(() => items.reduce((s, i) => s + i.spend, 0), [items]);

  function openInvoices() {
    const name = supplier.name || supplier.display_name;
    const tinParam = supplier.tin ? `&supplierTin=${encodeURIComponent(supplier.tin)}` : '';
    window.location.hash = `/invoices?from=${fromDate}&to=${toDate}&supplier=${encodeURIComponent(name)}${tinParam}`;
  }

  return (
    <section className="ui-stack">
      <button className="link" onClick={onBack} style={{ fontSize: 13, alignSelf: 'flex-start' }}>
        ← {t('suppliers.title')}
      </button>
      <PageHeader
        title={
          <>
            {supplier.display_name || supplier.name}
            {supplier.category && (
              <span className="badge" style={{ marginLeft: 10 }}>
                {t(`suppliers.cat_${supplier.category}`, supplier.category)}
              </span>
            )}
          </>
        }
        subtitle={
          <>
            {supplier.name && supplier.name !== supplier.display_name ? `${supplier.name} · ` : ''}
            {t('supplierDetail.tinPrefix', { tin: supplier.tin })}
          </>
        }
        actions={
          <Button variant="secondary" onClick={openInvoices}>
            {t('supplierDetail.viewInvoices')}
          </Button>
        }
      />

      <div className="toolbar" style={{ marginBottom: 0 }}>
        <DateRange value={range} onChange={setRange} size="sm" />
        {loading && <span className="muted">{t('common.loading')}</span>}
      </div>

      {error && <Callout tone="danger">{error}</Callout>}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        {[
          { label: t('supplierDetail.totalSpend'), value: fmtAMD(totalSpend, lang) },
          { label: t('supplierDetail.approvedInvoices'), value: String(invoiceCount) },
          { label: t('supplierDetail.itemsSupplied'), value: String(items.length) },
        ].map((card) => (
          <div key={card.label} className="panel" style={{ padding: '10px 16px', minWidth: 150 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{card.label}</div>
            <div style={{ fontSize: 20, fontWeight: 600, marginTop: 2 }}>{card.value}</div>
          </div>
        ))}
      </div>

      <div className="panel">
        {!loading && items.length === 0 ? (
          <div className="empty-state">
            {t('supplierDetail.empty')}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('supplierDetail.colItem')}</th>
                <th style={{ textAlign: 'right' }}>{t('supplierDetail.colQuantity')}</th>
                <th style={{ textAlign: 'right' }}>{t('supplierDetail.colInvoices')}</th>
                <th style={{ textAlign: 'right' }}>{t('supplierDetail.colAvgPrice')}</th>
                <th style={{ textAlign: 'right' }}>{t('supplierDetail.colLastPrice')}</th>
                <th>{t('supplierDetail.colTrend')}</th>
                <th style={{ textAlign: 'right' }}>{t('supplierDetail.colSpend')}</th>
                <th style={{ textAlign: 'right' }}>{t('supplierDetail.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const avg = item.qty > 0 ? item.spend / item.qty : 0;
                const last = item.points.length > 0 ? item.points[item.points.length - 1]!.price : 0;
                const changePct = avg > 0 && last > 0 ? ((last - avg) / avg) * 100 : 0;
                const highlight = Math.abs(changePct) >= 5 && item.points.length >= 2;
                return (
                  <tr key={item.key}>
                    <td>
                      {item.label}
                      {!item.tracked && (
                        <span
                          className="badge badge-muted"
                          style={{ marginLeft: 6, fontSize: 10 }}
                          title={t('supplierDetail.untracked')}
                        >
                          {t('supplierDetail.untracked')}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtQty(item.qty, lang)} {item.unit}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                      {item.invoiceIds.size}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {avg > 0 ? fmtPrice(avg, lang) : empty}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        color: highlight
                          ? changePct > 0
                            ? 'var(--danger)'
                            : 'var(--success, #10b981)'
                          : undefined,
                        fontWeight: highlight ? 600 : undefined,
                      }}
                    >
                      {last > 0 ? fmtPrice(last, lang) : empty}
                      {highlight && (
                        <span style={{ fontSize: 11, marginLeft: 4 }}>
                          {changePct > 0 ? '↑' : '↓'}
                          {Math.abs(changePct).toFixed(0)}%
                        </span>
                      )}
                    </td>
                    <td>
                      <Sparkline points={item.points} empty={empty} />
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtAMD(item.spend, lang)}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>
                      {itemsSpend > 0 ? `${((item.spend / itemsSpend) * 100).toFixed(1)}%` : empty}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
